import { createSelector } from '@reduxjs/toolkit';
import type { ID } from '@ianesthesie-kit/services';
import type { RootState } from '@/store';
import { doctorBffApi } from '../doctorBffApi.slice';
import { isSecretary, type MinimalDoctorDto } from '../doctorBff.model';
import { surgicalEventApi } from '../surgicalEvent/surgicalEvent.slice';
import { surgeryInfoApi } from '../surgicalEvent/surgeryInfo/surgeryInfo.slice';
import { billingApi, type BillingDoctor } from './billing.slice';
import { billingLineApi } from './billingLine.slice';
import { findConsultationLine } from './billingLine.utils';
import {
  selectBillingDoctor,
  selectCanSendInvoice,
  selectModifiedBrssTotal,
  selectPickedBillingDoctorId
} from './billing.slice.selectors';

const normalizeName = (name: string): string =>
  name
    .toLocaleLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .sort()
    .join(' ');

const selectIsConfirmingConsultation = createSelector(
  (state: RootState) => state.doctorBffApi.mutations,
  (mutations) =>
    Object.values(mutations).some(
      (mutation) =>
        mutation?.endpointName === 'confirmConsultationLine' &&
        mutation.status === 'pending'
    )
);

type BillingDoctorResolution = {
  ready: boolean;
  doctor?: BillingDoctor;
};

/** Resolves only from current queries, and checks the same resolution again when printing. */
export const createBillingDoctorSelectors = ({
  patientId,
  currentUser,
  receivablesEnabled
}: {
  patientId: ID;
  currentUser: MinimalDoctorDto | undefined;
  receivablesEnabled: boolean;
}) => {
  const selectResolution = createSelector(
    [
      surgicalEventApi.endpoints.activeSurgicalEvent.select({ patientId }),
      surgeryInfoApi.endpoints.surgeryInfo.select({ patientId }),
      doctorBffApi.endpoints.doctors.select(),
      billingLineApi.endpoints.billingLines.select({ patientId }),
      selectPickedBillingDoctorId,
      selectIsConfirmingConsultation
    ],
    (
      event,
      surgery,
      roster,
      lines,
      pickedId,
      confirming
    ): BillingDoctorResolution => {
      if (!event.isSuccess || !event.data) {
        return { ready: false };
      }
      let doctor: MinimalDoctorDto | undefined;
      if (!receivablesEnabled) {
        doctor = currentUser;
      } else {
        if (!roster.isSuccess || !lines.isSuccess || confirming) {
          return { ready: false };
        }
        const doctors = roster.data?.filter((d) => !isSecretary(d)) ?? [];
        const feeEventId = event.data.consultationOriginId ?? event.data.id;
        let id =
          findConsultationLine(lines.data, feeEventId)?.billingDoctorId ??
          pickedId;
        if (!id) {
          if (!surgery.isSuccess) {
            return { ready: false };
          }
          for (const name of surgery.data?.anesthesiologists ?? []) {
            const match = doctors.find(
              (d) =>
                normalizeName(`${d.firstName} ${d.lastName}`) ===
                normalizeName(name)
            );
            if (match) {
              id = match.id;
              break;
            }
          }
          id ??= doctors.find((d) => d.id === currentUser?.id)?.id;
        }
        doctor = doctors.find((d) => d.id === id);
      }
      return {
        ready: true,
        doctor:
          doctor && !isSecretary(doctor)
            ? {
                id: doctor.id,
                conventionStatus: doctor.conventionStatus,
                patientId,
                surgicalEventId: event.data.id
              }
            : undefined
      };
    }
  );

  const selectCanIssueInvoice = (state: RootState): boolean => {
    const resolution = selectResolution(state);
    const doctor = resolution.doctor;
    const issuer = selectBillingDoctor(state);
    if (
      !resolution.ready ||
      !doctor ||
      issuer?.id !== doctor.id ||
      issuer.conventionStatus !== doctor.conventionStatus ||
      issuer.patientId !== patientId ||
      issuer.surgicalEventId !== doctor.surgicalEventId
    ) {
      return false;
    }
    const builder = state.invoiceBuilder;
    const modifiers = billingApi.endpoints.modifiers.select()(state);
    if (
      builder.procedureReimbursements.some(
        ({ info }) =>
          info?.code &&
          (!modifiers.isSuccess ||
            builder.selectedModifiersByCode[info.code] === undefined)
      )
    ) {
      return false;
    }
    const brss = builder.invoiceCalculation.brssBased;
    if (
      builder.invoiceCalculation.selectedType === 'brssBased' &&
      brss.manualPrice == null &&
      brss.base !==
        (selectModifiedBrssTotal(
          modifiers.data ?? [],
          doctor.conventionStatus
        )(state) || undefined)
    ) {
      return false;
    }
    return selectCanSendInvoice(state);
  };

  return { selectResolution, selectCanIssueInvoice };
};
